import React, { useState, useEffect } from 'react';
import { Waves, Clock, Star, Flame, Sparkles } from 'lucide-react';
import PageShell from '../components/PageShell';
import { motion } from 'framer-motion';
import { authFetch, API } from '../utils/api';


export default function Dashboard({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch(`${API}/api/dashboard`);
        if (res.ok) setData(await res.json());
      } catch {} finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) {
    return (
      <PageShell title="Dashboard">
        <div className="flex items-center justify-center py-20">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
          </div>
        </div>
      </PageShell>
    );
  }

  const stats = [
    { icon: Waves, label: 'Sessions', value: data?.total_sessions || 0, color: 'text-primary' },
    { icon: Clock, label: 'Water time', value: `${Math.round((data?.total_minutes || 0) / 60)}h`, color: 'text-secondary' },
    { icon: Flame, label: 'Streak', value: `${data?.streak || 0}d`, color: 'text-accent' },
    { icon: Star, label: 'Avg rating', value: data?.avg_rating || '-', color: 'text-primary' },
    { icon: Sparkles, label: 'Aura', value: data?.aura || 0, color: 'text-secondary' },
  ];

  return (
    <PageShell title="Dashboard">
      <div className="p-5" data-testid="dashboard-page">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {stats.slice(0, 4).map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.08 }}
              className="bg-bg-subtle/80 border border-white/5 rounded-2xl p-4"
              data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}
            >
              <s.icon className={`w-4 h-4 ${s.color} mb-2`} />
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">{s.label}</p>
              <p className="font-heading text-2xl font-bold">{s.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Aura full-width */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.35 }}
          className="bg-secondary/5 border border-secondary/15 rounded-2xl p-4 mb-6 flex items-center gap-4"
          data-testid="stat-aura"
        >
          <Sparkles className="w-6 h-6 text-secondary flex-shrink-0" />
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Community Aura</p>
            <p className="font-heading text-2xl font-bold text-secondary">{data?.aura || 0}</p>
          </div>
          <p className="text-[10px] text-muted-foreground max-w-[120px] text-right">earned from spot contributions & reactions</p>
        </motion.div>

        {/* Recent Sessions */}
        <div>
          <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">Recent Sessions</p>
          {data?.recent_sessions?.length > 0 ? (
            <div className="space-y-2">
              {data.recent_sessions.map((s, i) => (
                <div
                  key={i}
                  className="bg-bg-subtle/50 border border-white/5 rounded-2xl p-4 flex items-center justify-between"
                  data-testid={`recent-session-${i}`}
                >
                  <div>
                    <p className="text-sm font-medium">{s.location}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{s.date} &middot; {s.duration_minutes}min</p>
                  </div>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className={`w-3 h-3 ${j < s.rating ? 'text-secondary fill-secondary' : 'text-muted/50'}`} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-bg-subtle/50 border border-white/5 rounded-2xl p-8 text-center">
              <p className="text-xs text-muted-foreground">no sessions yet. get out there!</p>
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
